export interface AnswerEngineEntry {
  question: string;
  answer: string;
}

export interface EntitySignal {
  term: string;
  description: string;
}

export const homepageAnswerEngineEntries: AnswerEngineEntry[] = [
  {
    question: 'What is ReturnCatalyst?',
    answer:
      'ReturnCatalyst is a private equity AI platform that helps automate deal diligence from CIM upload through IC memo generation and portfolio monitoring. PE firms use it to extract CIM data, build financial models, run AI investment committee simulations, generate cited IC memos, and monitor portfolio KPIs in structured dashboards.',
  },
  {
    question: 'What is private equity AI?',
    answer:
      'Private equity AI applies document intelligence, retrieval, reasoning, workflow automation, and financial analysis to the work PE firms perform across sourcing, diligence, IC approval, deal execution, and portfolio monitoring.',
  },
  {
    question: 'How does AI due diligence work in ReturnCatalyst?',
    answer:
      'A deal team uploads CIMs, financials, presentations, meeting notes, and related materials. ReturnCatalyst extracts structured data, indexes source documents for cited retrieval, runs market and transaction research, highlights risks, and turns the analysis into IC-ready outputs.',
  },
  {
    question: 'Which private equity workflows does ReturnCatalyst cover?',
    answer:
      'ReturnCatalyst covers CIM analysis, AI financial modeling, sector research, transaction discovery, IC committee simulation, IC memo automation, due diligence question tracking, IC presentations, and portfolio monitoring.',
  },
  {
    question: 'Who is ReturnCatalyst built for?',
    answer:
      'ReturnCatalyst is built for private equity general partners, managing partners, deal teams, portfolio operations teams, and fund operations leaders who need faster diligence and clearer portfolio visibility.',
  },
];

export const homepageAeoFaqs: AnswerEngineEntry[] = [
  {
    question: 'Can ReturnCatalyst analyze a CIM and create a financial model?',
    answer:
      'Yes. ReturnCatalyst extracts financial tables from CIMs, detects key metrics, infers formulas such as growth rates and margins, and generates downloadable financial models that deal teams can review and refine.',
  },
  {
    question: 'Does ReturnCatalyst generate investment committee memos?',
    answer:
      'Yes. ReturnCatalyst generates PE-grade IC memorandums with cited claims, financial analysis, sector context, transaction comparables, risk assessment, and recommendation sections aligned to committee findings.',
  },
  {
    question: 'How does ReturnCatalyst support AI investment committee simulation?',
    answer:
      'ReturnCatalyst uses multiple expert committee perspectives to pressure-test an investment thesis across finance, legal, credit, operating, sector, exit, and technology risk lenses before the real committee meeting.',
  },
  {
    question: 'Can ReturnCatalyst monitor portfolio company performance?',
    answer:
      'Yes. ReturnCatalyst supports portfolio dashboards, Google Sheets sync, KPI tracking, variance alerts, covenant monitoring, board-ready analytics, and multi-company roll-ups for portfolio operations teams.',
  },
  {
    question: 'Is ReturnCatalyst a generic AI chatbot?',
    answer:
      'No. Chat is one surface on a deal-lifecycle platform. Uploaded documents are indexed for page-level citation, financial tables become model-ready data, and the same deal context carries from CIM analysis through IC materials into portfolio monitoring, so answers trace back to the source document rather than to a general-purpose prompt.',
  },
  {
    question: 'How does ReturnCatalyst keep AI outputs grounded?',
    answer:
      'ReturnCatalyst grounds analysis in uploaded documents, retrieved source passages, structured financial data, and cited market research so investment teams can trace claims back to supporting evidence.',
  },
];

export const privateEquityAiEntitySignals: EntitySignal[] = [
  {
    term: 'Private equity AI',
    description:
      'AI workflows calibrated to the work PE teams actually perform across sourcing, diligence, investment committee preparation, fund operations, and portfolio value creation.',
  },
  {
    term: 'AI due diligence',
    description:
      'Automated extraction, research, risk detection, and cited analysis across CIMs, data rooms, management calls, and market sources.',
  },
  {
    term: 'CIM analysis software',
    description:
      'Software that parses Confidential Information Memorandums, extracts financial statements, detects formulas, and turns static PDFs into usable deal data.',
  },
  {
    term: 'IC memo automation',
    description:
      'AI-assisted creation of investment committee memorandums with source-backed financial, market, risk, valuation, and recommendation sections.',
  },
  {
    term: 'AI financial modeling',
    description:
      'Automated financial model generation from deal documents, including revenue growth, margin analysis, EBITDA bridges, sensitivities, and Excel exports.',
  },
  {
    term: 'Portfolio monitoring AI',
    description:
      'AI-assisted KPI tracking, variance detection, covenant monitoring, and board-ready analytics across portfolio companies.',
  },
  {
    term: 'Investment committee simulation',
    description:
      'AI-driven thesis pressure testing from multiple committee perspectives before a private equity investment decision.',
  },
  {
    term: 'Generative engine optimization for B2B SaaS',
    description:
      'Structuring visible, factual, source-aligned website content so AI answer engines can identify entities, capabilities, use cases, and canonical URLs.',
  },
];
